import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { useProject } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { AppIcon } from "@/components/AppIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Save, Upload, RotateCcw, ImageIcon, Trash2, Copy, ExternalLink, Building2 } from "lucide-react";
import { toast } from "sonner";

export const BRANDING = {
  loginAppName: "branding-login-name",
  loginDescription: "branding-login-description",
  loginExtraInfo: "branding-login-extra",
  loginSave: "branding-login-save",
  loginReset: "branding-login-reset",
  loginIconUpload: "branding-login-icon",
  loginIconRemove: "branding-login-icon-remove",
  appName: "branding-app-name",
  appSave: "branding-app-save",
  appIconUpload: "branding-app-icon",
  appIconRemove: "branding-app-icon-remove",
};

function IconUploader({ currentUrl, onUploaded, onRemove, testIdUpload, testIdRemove }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/icon", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.url);
      toast.success("Ikon diunggah");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload gagal");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4 p-4 border border-border rounded-md">
      <AppIcon url={currentUrl} size={64} />
      <div className="flex-1">
        <div className="text-sm font-medium">Ikon aplikasi</div>
        <div className="text-xs text-muted-foreground mt-1">PNG/JPG/WebP/SVG/ICO · max 2 MB · disarankan 256×256px</div>
        <div className="flex gap-2 mt-3">
          <input ref={fileRef} type="file" accept="image/*,.svg,.ico" onChange={onPick} className="hidden" data-testid={testIdUpload} />
          <Button variant="outline" size="sm" type="button" onClick={() => fileRef.current?.click()} disabled={uploading}>
            <Upload className="w-4 h-4 mr-1" /> {uploading ? "Mengunggah…" : "Pilih file"}
          </Button>
          {currentUrl && (
            <Button variant="ghost" size="sm" type="button" onClick={onRemove} data-testid={testIdRemove}>
              <Trash2 className="w-4 h-4 mr-1 text-destructive" /> Hapus ikon
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function LoginPanel() {
  const { login, refresh } = useBranding();
  const [form, setForm] = useState({ app_name: "", description: "", extra_info: "", icon_url: null });

  useEffect(() => {
    setForm({
      app_name: login.app_name || "",
      description: login.description || "",
      extra_info: login.extra_info || "",
      icon_url: login.icon_url || null,
    });
  }, [login]);

  const save = async () => {
    try {
      await api.patch("/settings/login", form);
      toast.success("Informasi login berhasil diperbarui");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset informasi login ke default?")) return;
    try {
      await api.delete("/settings/login");
      toast.success("Informasi login direset");
      await refresh();
    } catch (e) {
      toast.error("Gagal reset");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Form */}
      <div className="lg:col-span-3 space-y-5">
        <IconUploader
          currentUrl={form.icon_url}
          onUploaded={(url) => setForm({ ...form, icon_url: url })}
          onRemove={() => setForm({ ...form, icon_url: null })}
          testIdUpload={BRANDING.loginIconUpload}
          testIdRemove={BRANDING.loginIconRemove}
        />
        <div className="space-y-2">
          <Label>Nama aplikasi (title)</Label>
          <Input value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} required data-testid={BRANDING.loginAppName} />
        </div>
        <div className="space-y-2">
          <Label>Deskripsi singkat</Label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid={BRANDING.loginDescription} />
        </div>
        <div className="space-y-2">
          <Label>Informasi tambahan <span className="text-xs text-muted-foreground">(tagline, kontak, catatan)</span></Label>
          <Textarea value={form.extra_info} onChange={(e) => setForm({ ...form, extra_info: e.target.value })} rows={2} data-testid={BRANDING.loginExtraInfo} />
        </div>
        <div className="flex gap-2">
          <Button onClick={save} data-testid={BRANDING.loginSave}><Save className="w-4 h-4 mr-1" /> Simpan</Button>
          <Button variant="outline" onClick={resetDefaults} data-testid={BRANDING.loginReset}><RotateCcw className="w-4 h-4 mr-1" /> Reset default</Button>
        </div>
      </div>

      {/* Live Preview */}
      <div className="lg:col-span-2">
        <div className="overline text-muted-foreground mb-3">Live Preview</div>
        <div className="border border-border rounded-lg bg-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <AppIcon url={form.icon_url} size={40} />
            <div className="font-display font-bold text-xl">{form.app_name || "App name"}</div>
          </div>
          <div className="overline text-primary mb-2">Selamat datang</div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Masuk ke akun Anda</h2>
          <p className="mt-2 text-sm text-muted-foreground">{form.description || "Deskripsi aplikasi…"}</p>
          {form.extra_info && (
            <p className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">{form.extra_info}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function AppPanel() {
  const { app, refresh } = useBranding();
  const [form, setForm] = useState({ app_name: "", icon_url: null });

  useEffect(() => {
    setForm({ app_name: app.app_name || "", icon_url: app.icon_url || null });
  }, [app]);

  const save = async () => {
    try {
      await api.patch("/settings/app", form);
      toast.success("Nama aplikasi berhasil diperbarui");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 space-y-5">
        <IconUploader
          currentUrl={form.icon_url}
          onUploaded={(url) => setForm({ ...form, icon_url: url })}
          onRemove={() => setForm({ ...form, icon_url: null })}
          testIdUpload={BRANDING.appIconUpload}
          testIdRemove={BRANDING.appIconRemove}
        />
        <div className="space-y-2">
          <Label>Nama aplikasi (header dashboard)</Label>
          <Input value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} required data-testid={BRANDING.appName} />
        </div>
        <Button onClick={save} data-testid={BRANDING.appSave}>
          <Save className="w-4 h-4 mr-1" /> Simpan
        </Button>
      </div>

      <div className="lg:col-span-2">
        <div className="overline text-muted-foreground mb-3">Live Preview Header</div>
        <div className="border border-border rounded-lg bg-card p-4">
          <div className="flex items-center gap-3">
            <AppIcon url={form.icon_url} size={36} />
            <div>
              <div className="font-display font-bold text-lg leading-none">{form.app_name || "App"}</div>
              <div className="text-[10px] text-muted-foreground tracking-[0.15em] uppercase mt-1">Dashboard</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Branding() {
  return (
    <div>
      <PageHeader
        title="Login Page & Branding"
        overline="Master Admin"
        description="Atur identitas visual aplikasi: ikon, nama, deskripsi halaman login, dan header dashboard. Per-proyek white-label di tab Per Proyek."
      />

      <Tabs defaultValue="login" className="w-full">
        <TabsList>
          <TabsTrigger value="login" data-testid="branding-tab-login">
            <ImageIcon className="w-4 h-4 mr-1" /> Halaman Login (Global)
          </TabsTrigger>
          <TabsTrigger value="app" data-testid="branding-tab-app">
            <ImageIcon className="w-4 h-4 mr-1" /> Header Dashboard (Global)
          </TabsTrigger>
          <TabsTrigger value="project" data-testid="branding-tab-project">
            <Building2 className="w-4 h-4 mr-1" /> Per Proyek (White-label)
          </TabsTrigger>
        </TabsList>
        <TabsContent value="login" className="mt-6">
          <LoginPanel />
        </TabsContent>
        <TabsContent value="app" className="mt-6">
          <AppPanel />
        </TabsContent>
        <TabsContent value="project" className="mt-6">
          <ProjectBrandingPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProjectBrandingPanel() {
  const { projects, currentId, selectProject } = useProject();
  const [pid, setPid] = useState(currentId || (projects[0]?.id || ""));
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    app_name: "",
    icon_url: null,
    login_title: "",
    login_description: "",
    login_extra_info: "",
  });
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (currentId && !pid) setPid(currentId);
  }, [currentId, pid]);

  const fetch_ = async (projId) => {
    if (!projId) return;
    try {
      const { data } = await api.get(`/projects/${projId}/branding`);
      setData(data);
      const o = data.overrides || {};
      setForm({
        app_name: o.app_name || "",
        icon_url: o.icon_url || null,
        login_title: o.login_title || "",
        login_description: o.login_description || "",
        login_extra_info: o.login_extra_info || "",
      });
    } catch (e) {
      toast.error("Gagal memuat branding proyek");
    }
  };

  useEffect(() => { if (pid) fetch_(pid); }, [pid]);

  const onPickIcon = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads/icon", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, icon_url: up.url });
      toast.success("Ikon diunggah");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload gagal");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!pid) return;
    const payload = {};
    for (const k of Object.keys(form)) {
      const v = form[k];
      if (v !== "" && v !== null) payload[k] = v;
    }
    try {
      await api.patch(`/projects/${pid}/branding`, payload);
      toast.success("Branding proyek diperbarui");
      fetch_(pid);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  const clearOverrides = async () => {
    if (!pid) return;
    if (!window.confirm("Hapus semua override branding proyek ini? Akan kembali ke branding global.")) return;
    try {
      await api.patch(`/projects/${pid}/branding`, { clear: true });
      toast.success("Branding proyek direset ke global");
      fetch_(pid);
    } catch (err) {
      toast.error("Gagal");
    }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;
  const tenantUrl = pid ? `${window.location.origin}/login/p/${pid}` : "";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(tenantUrl); toast.success("Link tenant disalin"); } catch { toast.error("Gagal menyalin"); }
  };

  if (projects.length === 0) {
    return <div className="text-muted-foreground">Belum ada proyek.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px] space-y-1">
          <Label className="text-xs">Pilih proyek</Label>
          <Select value={pid} onValueChange={(v) => { setPid(v); selectProject(v); }}>
            <SelectTrigger data-testid="project-branding-select"><SelectValue placeholder="Pilih proyek" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {data && (
          <Badge variant="outline" className={data.source === "project" ? "border-primary text-primary" : "text-muted-foreground"}>
            {data.source === "project" ? "PER-PROYEK AKTIF" : "INHERIT GLOBAL"}
          </Badge>
        )}
      </div>

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-5">
            <div className="flex items-center gap-4 p-4 border border-border rounded-md">
              <AppIcon url={form.icon_url} size={64} />
              <div className="flex-1">
                <div className="text-sm font-medium">Ikon proyek</div>
                <div className="text-xs text-muted-foreground mt-1">Override ikon global. PNG/JPG/WebP/SVG · max 2 MB</div>
                <div className="flex gap-2 mt-3">
                  <input ref={fileRef} type="file" accept="image/*,.svg,.ico" onChange={onPickIcon} className="hidden" data-testid="project-branding-icon" />
                  <Button variant="outline" size="sm" type="button" onClick={() => fileRef.current?.click()} disabled={uploading}>
                    <Upload className="w-4 h-4 mr-1" /> {uploading ? "Mengunggah…" : "Pilih file"}
                  </Button>
                  {form.icon_url && (
                    <Button variant="ghost" size="sm" type="button" onClick={() => setForm({ ...form, icon_url: null })}>
                      <Trash2 className="w-4 h-4 mr-1 text-destructive" /> Hapus
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Nama aplikasi (dashboard)</Label>
                <Input value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} placeholder={data.app.app_name} data-testid="project-branding-appname" />
                <p className="text-xs text-muted-foreground">Kosongkan untuk pakai global</p>
              </div>
              <div className="space-y-2">
                <Label>Title halaman login</Label>
                <Input value={form.login_title} onChange={(e) => setForm({ ...form, login_title: e.target.value })} placeholder={data.login.app_name} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Deskripsi login</Label>
              <Textarea value={form.login_description} onChange={(e) => setForm({ ...form, login_description: e.target.value })} placeholder={data.login.description} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Informasi tambahan login</Label>
              <Textarea value={form.login_extra_info} onChange={(e) => setForm({ ...form, login_extra_info: e.target.value })} placeholder={data.login.extra_info} rows={2} />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={save} data-testid="project-branding-save"><Save className="w-4 h-4 mr-1" /> Simpan</Button>
              <Button variant="outline" onClick={clearOverrides}><RotateCcw className="w-4 h-4 mr-1" /> Reset ke global</Button>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div>
              <div className="overline text-muted-foreground mb-2">Tenant Login URL</div>
              <div className="flex gap-2">
                <code className="flex-1 px-3 py-2 bg-muted rounded-md text-xs font-mono break-all">{tenantUrl}</code>
                <Button variant="outline" size="sm" onClick={copyLink}><Copy className="w-3 h-3" /></Button>
                <a href={tenantUrl} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm"><ExternalLink className="w-3 h-3" /></Button>
                </a>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Bagikan link ini ke user/admin proyek — halaman login mereka akan menampilkan branding khusus.</p>
            </div>

            <div className="border border-border rounded-lg bg-card p-4">
              <div className="overline text-muted-foreground mb-3">Preview Login</div>
              <div className="flex items-center gap-3 mb-3">
                <AppIcon url={form.icon_url || data.login.icon_url} size={36} />
                <div className="font-display font-bold text-base">{form.login_title || data.login.app_name}</div>
              </div>
              <p className="text-sm">{form.login_description || data.login.description || "—"}</p>
              <p className="mt-2 text-xs text-muted-foreground">{form.login_extra_info || data.login.extra_info}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
